/** Data-driven building system — creates sprites, collision buffers, interior tile
 *  replacement, and alpha fade from sector config. All runtime values are derived
 *  from 5 config fields + the loaded texture + project constants. */
import Phaser from 'phaser';
import { TILE_SIZE } from '../utils/Constants';
import { clamp, lerp, degreesToRadians } from '../utils/Math';
import type { BuildingConfig, FootprintBlock } from './ConfigLoader';

/** Projection angle for 3/4 top-down view (degrees → radians at use site). */
const PROJECTION_ANGLE_DEG = 63.43;

/** Width of invisible collision buffers along wall faces (pixels). */
const BUFFER_WIDTH = 10;

/** Width of north-side collision buffers (pixels). Thinner than east/west to
 *  allow the player to walk closer to the north face of buildings. */
const NORTH_BUFFER_WIDTH = 0;

/** Pixel distance over which the building fades from opaque to floor alpha. */
const FADE_RANGE = 64;

/** Minimum alpha when the player is fully inside the building's visual bounds. */
const FADE_FLOOR = 0.25;

/** Floor tile index used to replace interior wall tiles under buildings. */
const FLOOR_TILE = 1;

/** Tint applied to interior floor tiles to darken them under buildings (0x000000–0xffffff). */
const INTERIOR_TINT = 0xbbbbbb;

interface BuildingEntry {
  sprites: Phaser.GameObjects.Image[];
  fadeLeft: number;
  fadeRight: number;
  fadeTop: number;
  fadeDepthY: number;
}

export class BuildingSystem {
  private scene: Phaser.Scene;
  private groundLayer: Phaser.Tilemaps.TilemapLayer;
  private buildings: BuildingEntry[] = [];
  private bufferGroup: Phaser.Physics.Arcade.StaticGroup;

  constructor(scene: Phaser.Scene, groundLayer: Phaser.Tilemaps.TilemapLayer) {
    this.scene = scene;
    this.groundLayer = groundLayer;
    this.bufferGroup = scene.physics.add.staticGroup();
  }

  /** Process one building config: create sprite, replace tiles, create collision buffers. */
  addBuilding(config: BuildingConfig, playerSprite: Phaser.GameObjects.GameObject): void {
    const texture = this.scene.textures.get(config.imageKey);
    const source = texture.getSourceImage();
    const sourceWidth = source.width;
    const sourceHeight = source.height;

    // Derive scale from desired world width and projection angle
    const scaleX = (config.widthInTiles * TILE_SIZE) / sourceWidth;
    const scaleY = scaleX / Math.sin(degreesToRadians(PROJECTION_ANGLE_DEG));

    const southernmostSouthRow = Math.max(...config.footprint.map(b => b.rowMax));
    const fadeDepthY = (southernmostSouthRow + 1) * TILE_SIZE;

    // Distinct south-row boundaries determine depth bands. For rectangular
    // buildings (one unique rowMax) this produces a single sprite. For L-shaped
    // buildings, each step gets its own depth so the player correctly renders
    // in front of upper portions and behind lower portions at the step.
    const southRows = [...new Set(config.footprint.map(b => b.rowMax))].sort((a, b) => a - b);
    const spriteTopWorld = config.centerY - sourceHeight * scaleY * 0.5;

    const sprites: Phaser.GameObjects.Image[] = [];
    let prevSourceY = 0;
    for (let i = 0; i < southRows.length; i++) {
      const cutWorldY = (southRows[i] + 1) * TILE_SIZE;
      const cutSourceY = Math.min(Math.round((cutWorldY - spriteTopWorld) / scaleY), sourceHeight);
      const bandH = cutSourceY - prevSourceY;
      if (bandH <= 0) { prevSourceY = cutSourceY; continue; }

      const s = this.scene.add.image(config.centerX, config.centerY, config.imageKey);
      s.setScale(scaleX, scaleY);
      s.setDepth((southRows[i] + 1) * TILE_SIZE + 0.5);
      s.setCrop(0, prevSourceY, sourceWidth, bandH);
      sprites.push(s);
      prevSourceY = cutSourceY;
    }

    // Remaining sprite below the last cut (building base/shadow below south wall)
    if (prevSourceY < sourceHeight) {
      const s = this.scene.add.image(config.centerX, config.centerY, config.imageKey);
      s.setScale(scaleX, scaleY);
      s.setDepth((southRows[southRows.length - 1] + 1) * TILE_SIZE + 0.5);
      s.setCrop(0, prevSourceY, sourceWidth, sourceHeight - prevSourceY);
      sprites.push(s);
    }

    // Replace interior tiles with floor (skip rowMax south perimeter for tile collision)
    this.clearInteriorTiles(config.footprint);

    // Create collision buffers from footprint edge analysis
    this.createBuffers(config.footprint);

    // Wire player collider (buffers are shared across all buildings in one static group)
    this.scene.physics.add.collider(playerSprite, this.bufferGroup);

    // Compute fade AABB from the full sprite display bounds
    const fadeLeft = config.centerX - sourceWidth * scaleX * 0.5;
    const fadeRight = fadeLeft + sourceWidth * scaleX;
    const fadeTop = spriteTopWorld;

    this.buildings.push({ sprites, fadeLeft, fadeRight, fadeTop, fadeDepthY });
  }

  /** Wire enemy collision with all building buffers. Call after enemy spawn. */
  wireEnemyColliders(enemyManager: { setBuildingBuffers(group: Phaser.Physics.Arcade.StaticGroup): void }): void {
    enemyManager.setBuildingBuffers(this.bufferGroup);
  }

  /** Run edge-penetration alpha fade for all buildings. */
  update(playerX: number, playerBottomY: number): void {
    for (const b of this.buildings) {
      if (playerX > b.fadeLeft && playerX < b.fadeRight &&
          playerBottomY > b.fadeTop && playerBottomY < b.fadeDepthY) {
        const penetration = Math.min(
          playerX - b.fadeLeft,
          b.fadeRight - playerX,
          playerBottomY - b.fadeTop,
          b.fadeDepthY - playerBottomY,
        );
        const t = clamp(penetration / FADE_RANGE, 0, 1);
        const alpha = lerp(1, FADE_FLOOR, t);
        for (const s of b.sprites) s.setAlpha(alpha);
      } else {
        for (const s of b.sprites) s.setAlpha(1);
      }
    }
  }

  /** Clean up all building sprites and collision buffers. */
  destroy(): void {
    for (const b of this.buildings) {
      for (const s of b.sprites) s.destroy();
    }
    this.buildings.length = 0;
    if (this.bufferGroup.children) {
      this.bufferGroup.clear(true, true);
    }
  }

  /** Replace interior wall tiles under buildings with collision-enabled floor tiles.
   *  Floor visual (correct when building alpha-fades) + wall collision (knockback
   *  safety — WallPhysicsSystem pushes entities back out if they breach buffers).
   *  South perimeter (rowMax) stays as tile 3 for natural tile collision. */
  private clearInteriorTiles(footprint: FootprintBlock[]): void {
    for (const block of footprint) {
      for (let r = block.rowMin; r <= block.rowMax; r++) {
        for (let c = block.colMin; c <= block.colMax; c++) {
          this.placeCollidingFloor(c, r);
        }
      }
    }

    // Handle shared rows between adjacent blocks: if a block's rowMax matches
    // another block's rowMin, clear interior cols on that row that are covered
    // by the upper block but NOT the south perimeter of the lower block.
    for (const upper of footprint) {
      for (const lower of footprint) {
        if (upper === lower) continue;
        if (upper.rowMax !== lower.rowMin) continue;
        const overlapColMin = Math.max(upper.colMin, lower.colMin);
        const overlapColMax = Math.min(upper.colMax, lower.colMax);
        for (let c = overlapColMin; c <= overlapColMax; c++) {
          this.placeCollidingFloor(c, upper.rowMax);
        }
      }
    }
  }

  /** Place a floor tile that retains collision — looks like floor, blocks like wall. */
  private placeCollidingFloor(col: number, row: number): void {
    const tile = this.groundLayer.putTileAt(FLOOR_TILE, col, row);
    tile.setCollision(true, true, true, true);
    tile.tint = INTERIOR_TINT;
  }

  /** Create invisible collision buffers along building walls from footprint edge analysis. */
  private createBuffers(footprint: FootprintBlock[]): void {
    const buffers: Array<[number, number, number, number]> = [];

    for (const block of footprint) {
      // East/west buffers extend from the building's north edge through the
      // full south row, bottom edge flush with the south tile face. Phaser's
      // tile separation snaps bodies to exact tile-edge Y, so a character
      // walking east along the south face sits at body.top == buffer.bottom
      // (no overlap, no pocket). Northbound walkers get continuous side-face
      // coverage with no ledge to snag on at the south row transition.
      const rowSpan = (block.rowMax - block.rowMin + 1) * TILE_SIZE;
      if (rowSpan <= 0) continue;

      // East buffer
      buffers.push([
        (block.colMax + 1) * TILE_SIZE,
        block.rowMin * TILE_SIZE,
        BUFFER_WIDTH,
        rowSpan,
      ]);

      // West buffer
      buffers.push([
        block.colMin * TILE_SIZE - BUFFER_WIDTH,
        block.rowMin * TILE_SIZE,
        BUFFER_WIDTH,
        rowSpan,
      ]);
    }

    // North edge buffers: for each block's rowMin, find columns NOT covered
    // by another block at rowMax+1 == this rowMin (i.e., exposed north face)
    for (const block of footprint) {
      const northCols = new Set<number>();
      for (let c = block.colMin; c <= block.colMax; c++) northCols.add(c);

      // Remove columns covered by adjacent blocks above
      for (const other of footprint) {
        if (other === block) continue;
        if (other.rowMax + 1 !== block.rowMin) continue;
        for (let c = other.colMin; c <= other.colMax; c++) northCols.delete(c);
      }

      // Generate buffers for remaining exposed column spans.
      // Extend each span by BUFFER_WIDTH on both ends so the north buffer
      // covers the corners where it meets east/west buffers — gives arcade
      // physics a single continuous surface instead of two perpendicular
      // bodies that wedge the player.
      if (northCols.size === 0) continue;
      const sortedCols = [...northCols].sort((a, b) => a - b);
      let spanStart = sortedCols[0];
      let spanEnd = sortedCols[0];
      for (let i = 1; i <= sortedCols.length; i++) {
        if (i < sortedCols.length && sortedCols[i] === spanEnd + 1) {
          spanEnd = sortedCols[i];
        } else {
          const x = spanStart * TILE_SIZE - NORTH_BUFFER_WIDTH;
          const w = (spanEnd - spanStart + 1) * TILE_SIZE + NORTH_BUFFER_WIDTH * 2;
          buffers.push([
            x,
            block.rowMin * TILE_SIZE - NORTH_BUFFER_WIDTH,
            w,
            NORTH_BUFFER_WIDTH,
          ]);
          if (i < sortedCols.length) {
            spanStart = sortedCols[i];
            spanEnd = sortedCols[i];
          }
        }
      }
    }

    // South perimeter uses tile collision — no buffers needed.

    // Create physics rectangles for each buffer, and flag overlapping tiles
    // so the AI pathfinds around them. Uses tile.properties.bufferBlocked
    // (not tile.collides) to avoid widening physics collision to full tile width.
    for (const [bx, by, bw, bh] of buffers) {
      const buf = this.scene.add.rectangle(bx + bw / 2, by + bh / 2, bw, bh);
      buf.setVisible(false);
      this.bufferGroup.add(buf);

      // Inflate tile-marking bounds by standard enemy body half-width (7px)
      // so A* penalizes tiles where the body edge would clip the buffer.
      const bodyHalf = 7;
      const colMin = Math.floor((bx - bodyHalf) / TILE_SIZE);
      const colMax = Math.floor((bx + bw - 1 + bodyHalf) / TILE_SIZE);
      const rowMin = Math.floor((by - bodyHalf) / TILE_SIZE);
      const rowMax = Math.floor((by + bh - 1 + bodyHalf) / TILE_SIZE);
      for (let r = rowMin; r <= rowMax; r++) {
        for (let c = colMin; c <= colMax; c++) {
          const tile = this.groundLayer.getTileAt(c, r);
          if (tile) {
            if (!tile.properties) tile.properties = {};
            tile.properties.bufferBlocked = true;
          }
        }
      }
    }
  }
}
